import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import Modal from '../../components/shared/Modal/Modal';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import Field from '../../components/shared/Field/Field';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import appState from '../../state/AppStateContainer';
import {
  ContextProject,
  EvaluationTagDto,
  StartEvaluationBody,
  StartEvaluationContext,
  serviceEvaluations,
} from '../../services/service-evaluations';
import { EvaluationContextType, EvaluationLimits } from '../../util/evaluationContract';
import { fromIsoDay, toIsoDay } from '../../util/bidRequestReminder';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import { stockholmToday } from '../page-supplier-evaluations/reviewFormat';
import ContextField, {
  ContextValue,
  EMPTY_CONTEXT,
  HISTORICAL_PROJECT,
  NO_PROJECT,
  projectOf,
} from './ContextField';
import RecipientField from './RecipientField';
import MissingLine from './MissingLine';
import QuestionPicker from './QuestionPicker';
import TagPicker from './TagPicker';
import {
  IdempotencyKey,
  RecipientOption,
  defaultDueDay,
  defaultTitle,
  evaluationErrorMessage,
  hasGlobalNotice,
  idempotencyKeyFor,
  isUnansweredSend,
  outcomeToast,
  toRecipientInputs,
} from './evaluationStart';
import styles from './app-evaluation-start.module.scss';

export type QuickEvaluationSupplier = { _id: string; name: string };

export type QuickEvaluationModalProps = {
  supplier: QuickEvaluationSupplier;
  onClose: () => void;
  onSent: (requestId: string) => void;
  onTagsChanged?: () => void;
};

const contextOf = (
  projects: ContextProject[],
  value: ContextValue,
): StartEvaluationContext | null => {
  if (value.choice === NO_PROJECT) return { type: EvaluationContextType.None };
  if (value.choice === HISTORICAL_PROJECT) {
    const name = value.historicalName.trim();
    const number = value.historicalNumber.trim();
    if (!name && !number) return null;
    return { type: EvaluationContextType.Historical, historical: { name, number } };
  }
  const project = projectOf(projects, value);
  return project ? { type: EvaluationContextType.Project, projectId: project._id } : null;
};

const contextName = (projects: ContextProject[], value: ContextValue) =>
  value.choice === HISTORICAL_PROJECT
    ? value.historicalName.trim() || value.historicalNumber
    : projectOf(projects, value)?.name ?? null;

const QuickEvaluationModal = ({
  supplier,
  onClose,
  onSent,
  onTagsChanged,
}: QuickEvaluationModalProps) => {
  const [t] = useTranslation('evaluations');
  const [projects, setProjects] = useState<ContextProject[]>([]);
  const [tags, setTags] = useState<EvaluationTagDto[]>([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [context, setContext] = useState<ContextValue>(EMPTY_CONTEXT);
  const [contextCleared, setContextCleared] = useState(false);
  const [questionIds, setQuestionIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [recipients, setRecipients] = useState<RecipientOption[]>([]);
  const [dueDay, setDueDay] = useState(() => fromIsoDay(defaultDueDay(new Date())));
  const [editedTitle, setEditedTitle] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [orgTagsChanged, setOrgTagsChanged] = useState(false);
  const idempotency = useRef<IdempotencyKey | null>(null);

  useEffect(() => {
    let mounted = true;
    let failureShown = false;
    const load = <T,>(call: () => Promise<T>, apply: (value: T) => void) =>
      call()
        .then(value => {
          if (mounted) apply(value);
        })
        .catch(error => {
          reportUnexpected(error);
          if (!mounted || failureShown || hasGlobalNotice(error)) return;
          failureShown = true;
          NotificationManager.error(
            t('quick.loadFailed', 'Det gick inte att hämta allt till Ny utvärdering.'),
          );
        });
    load(() => serviceEvaluations.getContexts(supplier._id), setProjects);
    load(
      () => serviceEvaluations.getTags(),
      loaded => {
        setTags(loaded);
        setTagsLoaded(true);
      },
    );
    return () => {
      mounted = false;
    };
  }, [supplier._id, t]);

  const title = editedTitle ?? defaultTitle(t, supplier.name, contextName(projects, context));
  const sendContext = contextOf(projects, context);
  const valid = Boolean(sendContext && questionIds.length && recipients.length);
  const missing = [
    !sendContext &&
      (context.choice === HISTORICAL_PROJECT
        ? t('quick.missing.historical', 'skriv projektnamn eller nummer')
        : t('quick.missing.project', 'välj projekt')),
    !questionIds.length && t('quick.missing.questions', 'välj minst en fråga'),
    !recipients.length && t('quick.missing.recipients', 'lägg till mottagare'),
  ].filter((item): item is string => Boolean(item));

  const edited =
    <T,>(apply: (value: T) => void) =>
    (value: T) => {
      setContextCleared(false);
      apply(value);
    };

  const send = async () => {
    if (!valid || sending || !sendContext) return;
    const body: Omit<StartEvaluationBody, 'idempotencyKey'> = {
      supplierId: supplier._id,
      areaId: sendContext.type === EvaluationContextType.Project ? context.areaId : null,
      context: sendContext,
      questionIds,
      tagIds,
      recipients: toRecipientInputs(recipients),
      dueDayKey: toIsoDay(dueDay),
      title: title.trim() || defaultTitle(t, supplier.name, contextName(projects, context)),
    };
    idempotency.current = idempotencyKeyFor(idempotency.current, JSON.stringify(body));
    setSending(true);
    try {
      const outcome = await serviceEvaluations.startEvaluation({
        ...body,
        idempotencyKey: idempotency.current.key,
      });
      if (!outcome.inProgress) idempotency.current = null;
      const toast = outcomeToast(t, outcome);
      NotificationManager[toast.kind](toast.message);
      if (outcome.requestIds.length === 1) onSent(outcome.requestIds[0]);
    } catch (error) {
      if (!isUnansweredSend(error)) idempotency.current = null;
      reportUnexpected(error);
      const message = evaluationErrorMessage(t, error);
      if (message) NotificationManager.error(message);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    if (sending) return;
    onClose();
    if (orgTagsChanged) onTagsChanged?.();
  };

  const changeOrgTags = (next: EvaluationTagDto[]) => {
    setTags(next);
    setOrgTagsChanged(true);
  };

  return (
    <Modal
      show
      unmountWhenHidden
      setShow={close}
      onCancelButtonClick={close}
      title={t('quick.title', 'Ny utvärdering')}
      buttonInfo={{
        label: t('quick.send', 'Skicka'),
        action: send,
        loaderShow: sending,
        disabled: !valid || sending,
      }}
    >
      <div className={styles.modalBody}>
        <p className={styles.subtitle} title={supplier.name}>
          {supplier.name}
        </p>
        <div className={styles.fields}>
          <ContextField
            projects={projects}
            value={context}
            onChange={edited(setContext)}
            onChoiceChange={(next, cleared) => {
              setContext(next);
              setContextCleared(cleared);
            }}
          />
          {contextCleared && (
            <InfoBox
              description={t(
                'quick.contextCleared',
                'Inköp och projektuppgifter har rensats eftersom du bytte projekt.',
              )}
            />
          )}
          <QuestionPicker value={questionIds} onChange={edited(setQuestionIds)} />
          <RecipientField
            noEmailFor={{
              userId: appState.getUserId(),
              email: appState.getUserEmail(),
              name: appState.getUserName(),
            }}
            value={recipients}
            onChange={edited(setRecipients)}
          />
          <DatePicker
            label={t('quick.dueDate', 'Sista svarsdag')}
            selected={dueDay}
            minDate={fromIsoDay(stockholmToday())}
            onChange={edited((next: Date | null) => next && setDueDay(next))}
          />
          <TagPicker
            value={tagIds}
            onChange={edited(setTagIds)}
            tags={tags}
            onTagsChange={changeOrgTags}
            tagsLoaded={tagsLoaded}
          />
          <Field
            label={t('quick.titleLabel', 'Titel')}
            value={title}
            onChange={edited((text: string) =>
              setEditedTitle(text.slice(0, EvaluationLimits.TitleMaxLength)),
            )}
          />
          <MissingLine items={missing} />
        </div>
      </div>
    </Modal>
  );
};

export default QuickEvaluationModal;
